import type { ReactNode, Ref } from 'react';
import { Box, Typography } from '@mui/material';
import InboxOutlined from '@mui/icons-material/InboxOutlined';

export interface EmptyStateProps {
  /** Illustration glyph. Defaults to an inbox icon; pass any node to override. */
  icon?: ReactNode;
  /** Headline (translated copy). */
  title: string;
  /** Optional supporting body (translated copy). */
  message?: ReactNode;
  /** Optional call-to-action (e.g. a button). */
  action?: ReactNode;
  /** Tighter padding for inline/table usage. */
  compact?: boolean;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Centered empty placeholder: glyph + headline + optional body and CTA. Used
 * for empty tables, no-results filters, and not-enabled modules.
 */
export function EmptyState({
  icon,
  title,
  message,
  action,
  compact = false,
  ref,
}: EmptyStateProps) {
  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1,
        p: compact ? '32px 24px' : '56px 32px',
        maxWidth: 420,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          mb: 0.75,
          color: 'text.secondary',
        }}
      >
        {icon ?? <InboxOutlined sx={{ fontSize: 26 }} />}
      </Box>
      <Typography component="div" sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
        {title}
      </Typography>
      {message && (
        <Typography component="div" sx={{ fontSize: 14, color: 'text.secondary' }}>
          {message}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
}
